import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Sparkles, CheckCircle2, Users, Loader2, Wand2 } from "lucide-react";
import { api, apiErr, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, StatusBadge, AiDraftNotice } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function TrainingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignSel, setAssignSel] = useState([]);
  const [aiTheme, setAiTheme] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [qForm, setQForm] = useState({ text: "", qtype: "single", options: ["", "", ""], correct: 0, answer_bool: true, explanation: "" });

  const load = useCallback(() => {
    api.get(`/trainings/${id}`).then(({ data }) => setT(data));
    api.get(`/assignments?training_id=${id}`).then(({ data }) => setAssignments(data));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.role === "staff"))); }, []);

  if (!t) return <Loading />;

  const addQuestion = async () => {
    if (!qForm.text) { toast.error("設問を入力してください"); return; }
    const payload = { text: qForm.text, qtype: qForm.qtype, points: 25, explanation: qForm.explanation, order: (t.questions?.length || 0) };
    if (qForm.qtype === "single" || qForm.qtype === "multiple") {
      payload.options = qForm.options.filter((o) => o.trim()).map((label, i) => ({ label, correct: qForm.qtype === "single" ? i === qForm.correct : false }));
    } else if (qForm.qtype === "truefalse") payload.answer_bool = qForm.answer_bool;
    await api.post(`/trainings/${id}/questions`, payload);
    toast.success("設問を追加しました");
    setQForm({ text: "", qtype: "single", options: ["", "", ""], correct: 0, answer_bool: true, explanation: "" });
    load();
  };

  const delQuestion = async (qid) => { await api.delete(`/questions/${qid}`); load(); };

  const doAssign = async () => {
    if (!assignSel.length) { toast.error("職員を選択してください"); return; }
    await api.post("/assignments", { training_id: id, user_ids: assignSel });
    toast.success("割り当てました");
    setAssignSel([]);
    load();
  };

  const assignAll = async () => {
    await api.post("/assignments", { training_id: id, all_office_staff: true, office_id: t.office_id });
    toast.success("事業所の全職員に割り当てました");
    load();
  };

  const markImplemented = async () => {
    await api.post(`/trainings/${id}/mark-implemented`);
    toast.success("実施済にしました");
    load();
  };

  const generateAI = async () => {
    if (!aiTheme) { toast.error("テーマを入力してください"); return; }
    setAiLoading(true); setAiResult(null);
    try {
      const { data } = await api.post("/ai/training", { theme: aiTheme, target: t.target || "全職員" });
      setAiResult(data);
    } catch (e) { toast.error(apiErr(e, "AI生成に失敗しました")); } finally { setAiLoading(false); }
  };

  const applyAiQuestions = async () => {
    const qs = aiResult?.data?.questions || [];
    for (let i = 0; i < qs.length; i++) {
      const q = qs[i];
      const qtype = q.qtype === "truefalse" ? "truefalse" : "single";
      const payload = { text: q.text, qtype, points: 25, explanation: q.explanation, order: (t.questions?.length || 0) + i };
      if (qtype === "single") {
        const opts = (q.options || []).map((label) => ({ label, correct: String(label).trim() === String(q.answer).trim() }));
        if (!opts.some((o) => o.correct) && opts.length) opts[0].correct = true;
        payload.options = opts;
      } else payload.answer_bool = String(q.answer).includes("正") || q.answer === true;
      await api.post(`/trainings/${id}/questions`, payload);
    }
    toast.success("AI設問を反映しました（管理者確認後に有効化されます）");
    load();
  };

  return (
    <div>
      <button onClick={() => navigate("/trainings")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> 研修一覧へ戻る
      </button>
      <PageHeader
        title={t.name}
        subtitle={t.purpose}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={t.status} />
            {t.status !== "実施済" && <Button variant="outline" onClick={markImplemented} data-testid="mark-implemented-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> 実施済にする</Button>}
          </div>
        }
      />

      <Tabs defaultValue="videos">
        <TabsList className="mb-4">
          <TabsTrigger value="videos" data-testid="tab-videos">動画・チャプター</TabsTrigger>
          <TabsTrigger value="quiz" data-testid="tab-quiz">理解度確認</TabsTrigger>
          <TabsTrigger value="assign" data-testid="tab-assign">割当・進捗</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI研修作成</TabsTrigger>
        </TabsList>

        {/* Videos */}
        <TabsContent value="videos">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">登録済み動画</h3>
              {t.master_id && <span className="text-xs text-slate-400">研修コンテンツ（マスター教材）由来・動画は運営が管理</span>}
            </div>
            {t.videos?.length ? t.videos.map((v) => (
              <div key={v.id} className="mb-4 pb-4 border-b border-slate-100 last:border-0">
                <div className="font-medium text-slate-700">{v.title}</div>
                <video src={fileUrl(v.url)} controls className="w-full rounded-lg mt-2 bg-black aspect-video" />
                <div className="mt-2 text-xs text-slate-400">チャプター：{v.chapters?.map((c) => c.title).join(" / ") || "なし"}</div>
              </div>
            )) : <p className="text-sm text-slate-400">動画が未登録です。年間計画で研修コンテンツを選択すると、マスター教材の動画が受講できるようになります。</p>}
          </div>
        </TabsContent>

        {/* Quiz */}
        <TabsContent value="quiz">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-3">設問一覧（{t.questions?.length || 0}問）</h3>
              {t.questions?.length ? t.questions.map((q, i) => (
                <div key={q.id} className="mb-3 pb-3 border-b border-slate-100 last:border-0 flex justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium text-slate-700">Q{i + 1}. {q.text}</div>
                    <div className="text-xs text-slate-400 mt-1">形式：{q.qtype}／配点：{q.points}</div>
                  </div>
                  <button onClick={() => delQuestion(q.id)} className="text-rose-400 hover:text-rose-600" data-testid={`del-q-${q.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              )) : <p className="text-sm text-slate-400">設問が未登録です</p>}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-3">設問を追加</h3>
              <div className="space-y-3">
                <div>
                  <Label>形式</Label>
                  <select value={qForm.qtype} onChange={(e) => setQForm({ ...qForm, qtype: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="qtype-select">
                    <option value="single">単一選択</option><option value="multiple">複数選択</option><option value="truefalse">○×</option><option value="text">記述</option>
                  </select>
                </div>
                <div><Label>設問</Label><Textarea data-testid="q-text-input" value={qForm.text} onChange={(e) => setQForm({ ...qForm, text: e.target.value })} className="mt-1" /></div>
                {(qForm.qtype === "single" || qForm.qtype === "multiple") && qForm.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {qForm.qtype === "single" && <input type="radio" checked={qForm.correct === i} onChange={() => setQForm({ ...qForm, correct: i })} />}
                    <Input value={o} onChange={(e) => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm({ ...qForm, options: opts }); }} placeholder={`選択肢${i + 1}`} />
                  </div>
                ))}
                {qForm.qtype === "truefalse" && (
                  <div className="flex gap-2">
                    <Button type="button" variant={qForm.answer_bool ? "default" : "outline"} onClick={() => setQForm({ ...qForm, answer_bool: true })}>正解：○</Button>
                    <Button type="button" variant={!qForm.answer_bool ? "default" : "outline"} onClick={() => setQForm({ ...qForm, answer_bool: false })}>正解：×</Button>
                  </div>
                )}
                <div><Label>解説</Label><Input value={qForm.explanation} onChange={(e) => setQForm({ ...qForm, explanation: e.target.value })} className="mt-1" /></div>
                <Button onClick={addQuestion} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="add-question-btn"><Plus className="w-4 h-4 mr-1" /> 設問を追加</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Assign */}
        <TabsContent value="assign">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800">職員に割り当て</h3>
                <Button size="sm" variant="outline" onClick={assignAll} data-testid="assign-all-btn"><Users className="w-4 h-4 mr-1" /> 事業所全員</Button>
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1">
                {staff.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={assignSel.includes(s.id)} onCheckedChange={(c) => setAssignSel(c ? [...assignSel, s.id] : assignSel.filter((x) => x !== s.id))} data-testid={`assign-check-${s.id}`} />
                    <span className="text-sm text-slate-700">{s.name}</span>
                    <span className="text-xs text-slate-400">{s.office_names?.join(",")}</span>
                  </label>
                ))}
              </div>
              <Button onClick={doAssign} className="bg-blue-600 hover:bg-blue-700 w-full mt-3" data-testid="assign-btn">選択した職員に割り当て</Button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-3">受講状況（{assignments.length}名）</h3>
              {assignments.length ? assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-700">{a.user_name}</span>
                  <StatusBadge status={a.status} />
                </div>
              )) : <p className="text-sm text-slate-400">まだ割り当てられていません</p>}
            </div>
          </div>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai">
          <div className="bg-white rounded-xl border border-slate-200 p-5 max-w-3xl">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-amber-500" /><h3 className="font-semibold text-slate-800">AIで研修案を作成</h3></div>
            <p className="text-sm text-slate-500 mb-4">テーマを入力すると、研修目的・構成・台本・理解度確認問題・所感設問の案を生成します。</p>
            <div className="flex gap-2">
              <Input value={aiTheme} onChange={(e) => setAiTheme(e.target.value)} placeholder="例：高齢化に伴う身体拘束の適正化" data-testid="ai-theme-input" />
              <Button onClick={generateAI} disabled={aiLoading} className="bg-amber-500 hover:bg-amber-600 shrink-0" data-testid="ai-generate-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} 生成
              </Button>
            </div>
            {aiResult && (
              <div className="mt-5 space-y-4">
                <AiDraftNotice />
                {aiResult.data ? (
                  <>
                    <div><div className="text-xs font-semibold text-slate-500 mb-1">研修目的</div><p className="text-sm text-slate-700 whitespace-pre-wrap">{aiResult.data.purpose}</p></div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-1">構成</div>
                      <ul className="list-disc pl-5 text-sm text-slate-700">{(aiResult.data.structure || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-1">章立て</div>
                      {(aiResult.data.chapters || []).map((c, i) => (
                        <div key={i} className="mb-2 p-3 rounded-lg bg-slate-50">
                          <div className="text-sm font-medium text-slate-700">{c.title}</div>
                          <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{c.description}</div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-1">理解度確認問題（{(aiResult.data.questions || []).length}問）</div>
                      {(aiResult.data.questions || []).map((q, i) => <div key={i} className="text-sm text-slate-700 py-1">Q{i + 1}. {q.text}</div>)}
                      <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700" onClick={applyAiQuestions} data-testid="apply-ai-questions">この設問を研修に反映</Button>
                    </div>
                  </>
                ) : <pre className="text-xs bg-slate-50 p-3 rounded whitespace-pre-wrap">{aiResult.raw}</pre>}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
